/**
 * courses.js — React Query hooks for course + module CRUD
 *
 * Endpoints:
 *   GET  /admin/courses                            — course list
 *   GET  /courses/:courseId                        — course detail + lessons
 *   GET  /admin/courses/:courseId/modules          — module list
 *   POST /instructor/courses                       — create course
 *   PUT  /admin/courses/:courseId                  — update course
 *   DELETE /admin/courses/:courseId                — delete course
 *   POST /admin/courses/:courseId/modules          — create module
 *   PUT  /admin/courses/:courseId/modules/:moduleId — update module
 *   DELETE /admin/courses/:courseId/modules/:moduleId — delete module
 *   PUT  /admin/courses/:courseId/modules/reorder  — bulk reorder modules
 *   POST /admin/courses/:courseId/modules/:moduleId/lessons/reorder — lesson reorder
 *   POST /instructor/lessons                       — create lesson
 *   PUT  /instructor/lessons/:lessonId             — update lesson
 *   DELETE /instructor/lessons/:lessonId           — delete lesson
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

// ─── Query keys ───────────────────────────────────────────────────────────────
export const courseKeys = {
  all: ['courses'],
  lists: () => [...courseKeys.all, 'list'],
  list: (filters) => [...courseKeys.lists(), filters],
  detail: (id) => [...courseKeys.all, 'detail', id],
  modules: (courseId) => [...courseKeys.all, 'modules', courseId],
  lessons: (courseId) => [...courseKeys.all, 'lessons', courseId],
};

// ─── Queries ──────────────────────────────────────────────────────────────────

/**
 * Fetch paginated course list with optional filters.
 * @param {object} filters — { search, category, level, is_interactive }
 */
export function useCourses(filters = {}) {
  return useQuery({
    queryKey: courseKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', '200');
      if (filters.search) params.set('search', filters.search);
      if (filters.category) params.set('category', filters.category);
      if (filters.level) params.set('level', filters.level);
      if (filters.is_interactive != null) params.set('is_interactive', String(filters.is_interactive));
      const res = await apiClient.get(`/admin/courses?${params.toString()}`);
      return res.data.courses ?? [];
    },
    staleTime: 30_000,
  });
}

/**
 * Fetch single course detail + lessons.
 */
export function useCourse(courseId) {
  return useQuery({
    queryKey: courseKeys.detail(courseId),
    queryFn: async () => {
      const res = await apiClient.get(`/courses/${courseId}`);
      return res.data; // { course, lessons }
    },
    enabled: !!courseId,
  });
}

/**
 * Fetch module list for a course.
 */
export function useCourseModules(courseId) {
  return useQuery({
    queryKey: courseKeys.modules(courseId),
    queryFn: async () => {
      const res = await apiClient.get(`/admin/courses/${courseId}/modules`);
      return res.data.modules ?? [];
    },
    enabled: !!courseId,
  });
}

// ─── Course mutations ─────────────────────────────────────────────────────────

/**
 * Create a new course (POST /instructor/courses).
 */
export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/instructor/courses', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

/**
 * Update an existing course (PUT /admin/courses/:courseId).
 */
export function useUpdateCourse(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.put(`/admin/courses/${courseId}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.lists() });
      qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
    },
  });
}

/**
 * Delete a course (DELETE /admin/courses/:courseId).
 */
export function useDeleteCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courseId) =>
      apiClient.delete(`/admin/courses/${courseId}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

// ─── Module mutations ─────────────────────────────────────────────────────────

/**
 * Create a module (POST /admin/courses/:courseId/modules).
 */
export function useCreateModule(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post(`/admin/courses/${courseId}/modules`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.modules(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
    },
  });
}

/**
 * Update a module (PUT /admin/courses/:courseId/modules/:moduleId).
 */
export function useUpdateModule(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleId, data }) =>
      apiClient
        .put(`/admin/courses/${courseId}/modules/${moduleId}`, data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.modules(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
    },
  });
}

/**
 * Delete a module (DELETE /admin/courses/:courseId/modules/:moduleId).
 * Lessons become orphaned (backend handles or reassigns).
 */
export function useDeleteModule(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moduleId) =>
      apiClient
        .delete(`/admin/courses/${courseId}/modules/${moduleId}`)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.modules(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.lessons(courseId) });
    },
  });
}

/**
 * Bulk reorder modules (PUT /admin/courses/:courseId/modules/reorder).
 * @param {string[]} orderedIds — module_id list in new order
 */
export function useReorderModules(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds) =>
      apiClient
        .put(`/admin/courses/${courseId}/modules/reorder`, { module_ids: orderedIds })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.modules(courseId) });
    },
  });
}

/**
 * Reorder lessons within a module (POST /admin/courses/:courseId/modules/:moduleId/lessons/reorder).
 * @param {{ moduleId: string, lessonIds: string[] }}
 */
export function useReorderLessons(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleId, lessonIds }) =>
      apiClient
        .post(
          `/admin/courses/${courseId}/modules/${moduleId}/lessons/reorder`,
          { lesson_ids: lessonIds },
        )
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.modules(courseId) });
    },
  });
}

// ─── Lesson mutations ─────────────────────────────────────────────────────────

/**
 * Create a lesson (POST /instructor/lessons).
 */
export function useCreateLesson(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) =>
      apiClient.post('/instructor/lessons', { ...data, course_id: courseId }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.modules(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}

/**
 * Update a lesson (PUT /instructor/lessons/:lessonId).
 */
export function useUpdateLesson(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lessonId, data }) =>
      apiClient.put(`/instructor/lessons/${lessonId}`, data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.modules(courseId) });
    },
  });
}

/**
 * Delete a lesson (DELETE /instructor/lessons/:lessonId).
 */
export function useDeleteLesson(courseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lessonId) =>
      apiClient.delete(`/instructor/lessons/${lessonId}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseKeys.detail(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.modules(courseId) });
      qc.invalidateQueries({ queryKey: courseKeys.lists() });
    },
  });
}
